import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Menu as MenuIcon, X } from "lucide-react";
import Logo from "./Logo";

const NAV_ITEMS = [
  { to: "/info", label: "Cos'è" },
  { to: "/guida", label: "Guida" },
  { to: "/pricing", label: "Prezzi" },
  { to: "/legale", label: "Legalità" },
  { to: "/contatti", label: "Contatti" },
];

/**
 * Sticky header used on all public pages (home, info, pricing, guide, contact, legal,
 * login, register, password recovery). Renders a single source of truth for the
 * top nav.
 *
 * Props:
 * - ctaTo (string, default "/login"): destination of the rightmost CTA button.
 * - ctaLabel (string, default "Accedi"): visible label of CTA.
 */
export default function PublicHeader({ ctaTo = "/login", ctaLabel = "Accedi" }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="public-header-brand">
          <Logo size={26} withWordmark />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_ITEMS.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`public-nav-${n.to.replace(/\W/g, "")}`}
              className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {n.label}
            </Link>
          ))}
          <Link
            to={ctaTo}
            data-testid="public-nav-cta"
            className="ml-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all inline-flex items-center gap-1.5"
          >
            {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </nav>

        {/* Mobile burger */}
        <button
          className="md:hidden p-2 text-foreground"
          onClick={() => setMobileOpen((s) => !s)}
          aria-label={mobileOpen ? "Chiudi menu" : "Apri menu"}
          data-testid="public-nav-mobile-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <nav className="md:hidden border-t border-border bg-background" data-testid="public-nav-mobile">
          <div className="max-w-6xl mx-auto px-4 py-2 flex flex-col">
            {NAV_ITEMS.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`public-nav-mobile-${n.to.replace(/\W/g, "")}`}
                className="py-3 text-sm text-foreground hover:text-primary border-b border-border/60 last:border-0"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to={ctaTo}
              onClick={() => setMobileOpen(false)}
              className="mt-3 mb-2 px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-md text-center"
            >
              {ctaLabel}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
